<template>
  <Modal position="center" width="81.188rem" :framelessBody="true" ref="modalRef">
    <template #header>Photo gallery</template>
    <template #body>
      <FileViewerContainer v-model:activeIndex="activeIndex" :files="files" />
    </template>
    <template #footer>
      <View class="footer" align="center" justify="space-between" gap="sm">
        <Text variant="label-medium">{{ imageProgressionCounter }}</Text>

        <slot name="actions" :active-file="activeFile" :active-index="activeIndex" />
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, readonly, watch } from 'vue';

import { Modal, Text, View } from '@tallo/design-system';

import { FileDto } from '../interfaces/file.dto';
import FileViewerContainer from './file-viewer-container.vue';

const props = defineProps<{ files: FileDto[] }>();
const activeIndex = ref(-1);
const modalRef = ref<InstanceType<typeof Modal>>();

const fileItems = ref<FileDto[]>([]);
const activeFile = computed(() => props.files[activeIndex.value]);
const filesCount = computed(() => props.files.length);
const imageProgressionCounter = computed(() => `Photo ${activeIndex.value + 1}/${filesCount.value}`);
const isVisible = computed(() => modalRef.value?.isVisible);

function show(index: number = 0) {
  setActiveIndex(index);
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
  setActiveIndex(-1);
}

function setActiveIndex(index: number) {
  activeIndex.value = index;
}

// This watcher sets changed value of props.files to fileItems to be able to track old value of the array in the next watcher.
// Otherwise old value in the next array will be always the same.
watch(
  props.files,
  (newValue) => {
    if (Array.isArray(newValue)) {
      fileItems.value = JSON.parse(JSON.stringify(newValue));
    }
  },
  {
    deep: true,
    immediate: true,
  },
);

watch(
  fileItems,
  (newValue, oldValue) => {
    if (activeIndex.value < 0 || !oldValue?.length) {
      return;
    }

    if (newValue[activeIndex.value]?.id === oldValue[activeIndex.value]?.id) {
      return;
    }

    if (newValue.length === 0) {
      return close();
    }

    if (activeIndex.value >= newValue.length) {
      setActiveIndex(newValue.length - 1);
    }
  },
  { deep: true },
);

defineExpose({
  show,
  close,
  isVisible,
  modalRef,
  activeIndex: readonly(activeIndex),
});
</script>

<style scoped lang="scss">
.footer {
  width: 100%;
}
</style>
